import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { warn } from "@/lib/log";
import { deleteDraft, loadDraft, saveDraft } from "@/lib/offlineDrafts";
import { addSectionFile, allIssues, emptyVisitForm, mergeDraft, toPayload } from "@/lib/visitSchema";

/* Form state for the inspection workflow: IndexedDB draft restore + debounced autosave + submit. */
export default function useVisitForm({ lead, online, uploadsRef, onSaved }) {
  const [form, setForm] = useState(() => emptyVisitForm(lead));
  const [busy, setBusy] = useState(false);
  const [draftLoaded, setDraftLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    loadDraft(lead.id)
      .then((d) => { if (alive && d?.form) setForm((prev) => mergeDraft(prev, d.form)); })
      .catch((err) => warn("draft load failed", err))
      .finally(() => { if (alive) setDraftLoaded(true); });
    return () => { alive = false; };
  }, [lead.id]);

  useEffect(() => {
    if (!draftLoaded) return undefined;
    const h = setTimeout(() => { saveDraft(lead.id, form, form.property_photos.map((p) => p.file_id)).catch(() => {}); }, 500);
    return () => clearTimeout(h);
  }, [form, lead.id, draftLoaded]);

  const submit = async () => {
    if (!online) { toast.warning("You are offline. Draft saved locally — submit when back online."); return; }
    setBusy(true);
    try {
      let final = form;
      for (const a of await uploadsRef.current.syncPending()) final = addSectionFile(final, a.section, a.file_id, a.tag);
      const remaining = allIssues(final);
      if (remaining.length) { toast.error(remaining[0]); return; }
      await api.post(`/leads/${lead.id}/visit`, toPayload(final));
      await deleteDraft(lead.id);
      toast.success("Inspection submitted");
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return { form, setForm, busy, submit };
}
